import { useCallback, useEffect, useState } from "react";

const ROUND_LOCK_KEY = "pfjudgepro-round-started";
const apiUrl = process.env.REACT_APP_BACKEND_URL;

export default function useRoundLock(userId) {
  const [isRoundStarted, setIsRoundStarted] = useState(false);
  const [isRoundEnded, setIsRoundEnded] = useState(false);
  const [isRoundLockHydrated, setIsRoundLockHydrated] = useState(false);

  useEffect(() => {
    if (!userId) {
      setIsRoundLockHydrated(false);
      return;
    }
    const key = `${ROUND_LOCK_KEY}-${userId}`;
    const storedState = sessionStorage.getItem(key);
    setIsRoundStarted(storedState === "true" || storedState === "ended");
    setIsRoundEnded(storedState === "ended");
    setIsRoundLockHydrated(true);
  }, [userId]);

  useEffect(() => {
    if (!userId || !isRoundLockHydrated) return;
    const key = `${ROUND_LOCK_KEY}-${userId}`;
    if (isRoundEnded) {
      sessionStorage.setItem(key, "ended");
    } else if (isRoundStarted) {
      sessionStorage.setItem(key, "true");
    } else {
      sessionStorage.removeItem(key);
    }
  }, [isRoundEnded, isRoundLockHydrated, isRoundStarted, userId]);

  const updateServerState = useCallback(async (action) => {
    if (!userId) return false;
    try {
      const response = await fetch(`${apiUrl}/api/round-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action }),
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }, [userId]);

  const resetRoundLock = useCallback(() => {
    setIsRoundStarted(false);
    setIsRoundEnded(false);
    void updateServerState("reset");
  }, [updateServerState]);

  const startRoundLock = useCallback(async () => {
    if (isRoundStarted) return true;
    if (!await updateServerState("start")) return false;
    setIsRoundStarted(true);
    return true;
  }, [isRoundStarted, updateServerState]);

  const endRoundLock = useCallback(async () => {
    if (!isRoundStarted || isRoundEnded) return false;
    if (!await updateServerState("end")) return false;
    setIsRoundEnded(true);
    return true;
  }, [isRoundEnded, isRoundStarted, updateServerState]);

  return {
    isRoundLockHydrated,
    isRoundEnded,
    isRoundStarted,
    endRoundLock,
    resetRoundLock,
    startRoundLock,
  };
}